import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { invoiceApi } from '@/entities/invoice';
import {
    isGenerating,
    isReleased,
    pdfProblemOf,
    reportApi,
    reportKeys,
    reportPdfFilename,
    type OrderReport,
    type Report,
    type ReportDeliveredLink,
    type ReportFindingAdjustmentInput,
    type ReportInvoiceChoice,
} from '@/entities/report';
import { tenantApi } from '@/entities/tenant';
import { errorMessage, isApiError, problemKey } from '@/shared/api';

import { offerDownload, openInNewTab } from './browser';
import { reportCopy } from './copy';

/** Zo vaak kijkt het scherm of een concept in de wachtrij al klaar is. */
export const GENERATION_POLL_MS = 2_000;

/** De stand van de factuur van een keuring, gezien vanaf het rapport. */
export const orderInvoiceKey = (orderUuid: string) => ['invoices', 'for-order', orderUuid] as const;

/**
 * Alles wat het rapportscherm met de API doet: het rapport van de keuring
 * lezen (en blijven lezen zolang hij genereert), genereren, teksten en bedragen
 * bijstellen, akkoord geven, goedkeuren, opnieuw versturen en de PDF ophalen.
 *
 * Elk antwoord met een rapport komt meteen in de cache, zodat het scherm de
 * stand van de server toont zonder nog een keer te vragen.
 *
 * `invoice` vraagt op hoe de factuur van de keuring ervoor staat - alleen als een
 * verstuurdialoog open staat en deze persoon facturen mag meesturen. Elke keer dat de
 * dialoog opent opnieuw: een factuur kan intussen in Facturatie verstuurd zijn.
 */
export function useReportScreen(orderUuid: string, options: { invoice?: boolean } = {}) {
    const queryClient = useQueryClient();
    const key = reportKeys.forOrder(orderUuid);

    const report = useQuery({
        queryKey: key,
        queryFn: () => reportApi.forOrder(orderUuid),
        enabled: orderUuid !== '',
        staleTime: 0,
        refetchInterval: (query) => {
            const data = query.state.data;

            return data && isGenerating(data.report) ? GENERATION_POLL_MS : false;
        },
    });

    /**
     * Verstuurt het bedrijf zonder nalezen (`autoSendReport`), dan gaat het concept
     * dat vanzelf volgt op een ingediende opname meteen de deur uit. Een versie die
     * hier gevraagd wordt niet (`send: false`); het scherm zegt dat bij het genereren.
     */
    const settings = useQuery({
        queryKey: [...reportKeys.all, 'tenant-settings'],
        queryFn: () => tenantApi.settings(),
        staleTime: 0,
    });

    /**
     * Een nieuwe stand van de nieuwste versie. Is die (nu) goedgekeurd, dan heeft
     * de koper voortaan deze versie; anders blijft de verstuurde versie staan.
     *
     * `link` is de link die het antwoord meegaf, zodat "Link kopiëren" er meteen
     * staat na goedkeuren of versturen — hij verandert niet meer, want een
     * gecorrigeerde versie neemt de link van haar voorganger over.
     */
    const store = (next: Report, link?: ReportDeliveredLink) => {
        queryClient.setQueryData<OrderReport | null>(key, (current) => {
            const released = current?.released ?? null;
            const kept = released?.reportUuid === next.uuid ? released.link : null;

            return {
                report: next,
                released: isReleased(next)
                    ? {
                          reportUuid: next.uuid,
                          version: next.version,
                          link:
                              link === undefined
                                  ? kept
                                  : {
                                        ...(kept ?? emptyShare(next.uuid)),
                                        uuid: link.shareUuid,
                                        reportUuid: next.uuid,
                                        url: link.url,
                                        state: 'active',
                                        validUntil: link.validUntil,
                                    },
                      }
                    : released,
            };
        });
    };

    /** Na een conflict weet de server het beter: lees opnieuw. */
    const refreshOnConflict = (error: unknown) => {
        if (isApiError(error) && error.status === 409) {
            void queryClient.invalidateQueries({ queryKey: key });
        }
    };

    const generate = useMutation({
        mutationFn: () => reportApi.generate(orderUuid),
        onSuccess: (result) => store(result.report),
        onError: refreshOnConflict,
    });

    const saveSection = useMutation({
        mutationFn: (input: { reportUuid: string; sectionUuid: string; body: string | null }) =>
            reportApi.updateSection(input.reportUuid, input.sectionUuid, input.body),
        onSuccess: (next: Report) => store(next),
        onError: refreshOnConflict,
    });

    /** Het geld van één bevinding; het antwoord is het opnieuw opgebouwde concept. */
    const adjustFinding = useMutation({
        mutationFn: (input: {
            reportUuid: string;
            findingKey: string;
            adjustment: ReportFindingAdjustmentInput;
        }) => reportApi.adjustFinding(input.reportUuid, input.findingKey, input.adjustment),
        onSuccess: (next: Report) => store(next),
        onError: refreshOnConflict,
    });

    /** Het akkoord van de inspecteur met een bedrag dat een ander aanpaste. */
    const confirmFinding = useMutation({
        mutationFn: (input: { reportUuid: string; findingKey: string }) =>
            reportApi.confirmFinding(input.reportUuid, input.findingKey),
        onSuccess: (next: Report) => store(next),
        onError: refreshOnConflict,
    });

    /** Een verstuurd rapport weer openzetten: een concept met dezelfde inhoud. */
    const revise = useMutation({
        mutationFn: (reportUuid: string) => reportApi.revise(reportUuid),
        onSuccess: (next: Report) => store(next),
        onError: refreshOnConflict,
    });

    const orderInvoice = useQuery({
        queryKey: orderInvoiceKey(orderUuid),
        queryFn: () => invoiceApi.forOrder(orderUuid),
        enabled: orderUuid !== '' && options.invoice === true,
        staleTime: 0,
        retry: false,
    });

    /** Na versturen staat de factuur anders: een concept kreeg een nummer, of de klant heeft haar. */
    const refreshInvoice = () =>
        void queryClient.invalidateQueries({ queryKey: orderInvoiceKey(orderUuid) });

    const approve = useMutation({
        mutationFn: (input: {
            reportUuid: string;
            send: boolean;
            invoice?: ReportInvoiceChoice;
            language?: string;
        }) =>
            reportApi.approve(input.reportUuid, {
                send: input.send,
                ...(input.invoice ? { invoice: input.invoice } : {}),
                ...(input.language ? { language: input.language } : {}),
            }),
        onSuccess: (approval) => {
            store(approval.report, approval.link);
            refreshInvoice();
        },
        onError: refreshOnConflict,
    });

    const send = useMutation({
        mutationFn: (input: {
            reportUuid: string;
            invoice?: ReportInvoiceChoice;
            language?: string;
        }) =>
            reportApi.send(input.reportUuid, {
                ...(input.invoice ? { invoice: input.invoice } : {}),
                ...(input.language ? { language: input.language } : {}),
            }),
        onSuccess: (result) => {
            store(result.report, result.link);
            refreshInvoice();
        },
        onError: refreshOnConflict,
    });

    /** `view` opent de PDF in een tabblad, `download` bewaart hem. */
    const pdf = useMutation({
        mutationFn: async (input: { report: Report; mode: 'download' | 'view' }) => {
            const { blob } = await reportApi.pdf(input.report.uuid);

            return { blob, filename: reportPdfFilename(input.report), mode: input.mode };
        },
        onSuccess: ({ blob, filename, mode }) => {
            if (mode === 'download' || !openInNewTab(blob)) {
                offerDownload(blob, filename);
            }
        },
        onError: refreshOnConflict,
    });

    /**
     * Wat de server over zijn PDF-dienst zegt, en hoeveel verstuurde rapporten
     * nog op hun PDF wachten. Pas opgehaald als een download stukliep op een
     * dienst die er niet is: dan helpt opnieuw proberen niet en wil de eigenaar
     * weten hoe groot het is.
     */
    const pdfService = useQuery({
        queryKey: reportKeys.pdfService,
        queryFn: () => reportApi.pdfService(),
        enabled: pdfProblemOf(pdf.error) === 'setup',
        staleTime: 60_000,
    });

    return {
        report,
        settings,
        orderInvoice,
        pdfService,
        generate,
        revise,
        saveSection,
        adjustFinding,
        confirmFinding,
        approve,
        send,
        pdf,
    };
}

/** De tellers van een link die dit scherm nog niet heeft gezien. */
function emptyShare(reportUuid: string) {
    return {
        uuid: '',
        reportUuid,
        url: null,
        state: 'active' as const,
        validUntil: '',
        showCosts: true,
        allowForward: true,
        accessCodeRequired: false,
        openedCount: 0,
        downloadedCount: 0,
        forwardedCount: 0,
        lastOpenedAt: null,
        revokedAt: null,
        createdAt: null,
    };
}

/**
 * De zin onder een mislukte actie. Een bekend probleem krijgt een eigen,
 * gewone uitleg; de rest valt terug op de melding van de API.
 */
export function reportProblemMessage(error: unknown): string {
    switch (problemKey(error)) {
        case 'report-not-generatable': {
            const reason = isApiError(error) ? error.problem.reason : undefined;

            return (
                (typeof reason === 'string' ? reportCopy.notGeneratable[reason] : undefined) ??
                errorMessage(error)
            );
        }
        case 'report-already-approved':
            return reportCopy.alreadyApproved;
        case 'report-without-recipients':
            return reportCopy.withoutRecipients;
        case 'report-not-ready':
            return reportCopy.notReady;
        case 'report-locked':
            return reportCopy.locked;
        case 'report-not-approved':
            return reportCopy.notApproved;
        case 'report-not-approvable':
            return isApiError(error) && error.problem.reason === 'adjustments_await_inspector'
                ? reportCopy.awaitingInspectorApprove
                : isApiError(error) && error.problem.reason === 'inspection_not_submitted'
                  ? reportCopy.surveyOpenBody
                  : errorMessage(error);
        case 'finding-not-adjustable':
            return isApiError(error) && error.problem.reason === 'survey_open'
                ? reportCopy.adjustSurveyOpen
                : errorMessage(error);
        case 'pdf-unavailable':
            return pdfProblemOf(error) === 'setup'
                ? reportCopy.pdfNotConfigured
                : reportCopy.pdfUnavailable;
        default:
            return errorMessage(error);
    }
}
